/* ============================================================================
 * useHistoryManagement.ts - History and snapshot management functions
 * ============================================================================ */

import { useCallback, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import type { ProjectData } from '@/shared/types/projectData';
import { undoStack } from '@/shared/commands/UndoStack';
import type { SnapshotHistory } from '@/shared/utils/snapshotHistory';

interface UseHistoryManagementParams {
  projectData: ProjectData | null;
  setError: Dispatch<SetStateAction<string | null>>;
  snapshotHistory: MutableRefObject<SnapshotHistory>;
}

/**
 * Hook for history and snapshot management
 */
export function useHistoryManagement({
  projectData,
  setError,
  snapshotHistory
}: UseHistoryManagementParams) {
  
  // Clear history function - clears undo/redo stack
  const clearHistory = useCallback(async () => {
    try {
      // Clear the client-side undo stack
      undoStack.clear();

      // Clear snapshot history as well so diagnostics/history panels stay coherent
      snapshotHistory.current.clear();
      
      // i18n-raw-ui-literal-exempt: Internal command result metadata, not rendered product UI.
      return { success: true, message: 'Undo/redo history cleared' };
    } catch (error) {
      const errorMessage = `Clear history failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
      return { success: false, error: errorMessage };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData, setError, snapshotHistory]);
  
  // Snapshot performance monitoring functions
  const getSnapshotPerformanceReport = useCallback(() => {
    return snapshotHistory.current.getPerformanceReport();
  }, [snapshotHistory]);

  const configureSnapshotHistory = useCallback((options: {
    maxSnapshots?: number;
    enableCompression?: boolean;
    enablePerformanceMonitoring?: boolean;
    backgroundProcessing?: boolean;
  }) => {
    snapshotHistory.current.configure(options);
  }, [snapshotHistory]);
  
  return {
    clearHistory,
    getSnapshotPerformanceReport,
    configureSnapshotHistory
  };
}
